import { AnimatePresence } from 'motion/react';
import { FileTextIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { FileDropzone } from '@/shared/ui/FileDropzone';
import { FileListItem } from '@/shared/ui/FileListItem';
import type { FileListItemStatus } from '@/shared/ui/FileListItem/FileListItem.types';
import { formatFileSize } from '@/shared/utils/format';
import type { UploadChipStatus, UploadViewProps } from './UploadView.types';
import { useTranslation } from 'react-i18next';

/**
 * Map an `UploadChipStatus` (queue domain) to a `FileListItemStatus`
 * (presentation domain). `FileListItem` lives in `shared/` and is reused
 * by other consumers, so we keep its union narrow and translate locally
 * instead of widening it with queue-specific states.
 *
 * `'cancelled'` maps to `'pending'`: a user cancel is intentional, not
 * an error — render the row as a fresh neutral chip (no destructive
 * styling, remove button enabled). The user can dismiss the chip with
 * the X or re-trigger an upload via the dropzone.
 */
function toPresentationStatus(status: UploadChipStatus): FileListItemStatus {
  return status === 'cancelled' ? 'pending' : status;
}

const ARTIFACT_ACCEPTED_FORMATS: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt'],
  'application/rtf': ['.rtf'],
};

const ARTIFACT_MAX_SIZE = 10 * 1024 * 1024;

export function UploadView(props: UploadViewProps) {
  const { t } = useTranslation('projects');

  const { chips, warnings, isAnyUploading, isPending, onDrop, onRemove, onDismissWarning } = props;

  return (
    <div data-testid="upload-view" className="flex flex-1 min-h-0 flex-col gap-4">
      <FileDropzone
        accept={ARTIFACT_ACCEPTED_FORMATS}
        maxSize={ARTIFACT_MAX_SIZE}
        disabled={isAnyUploading}
        buttonDisabled={isAnyUploading || isPending}
        title={t('projectDetail.uploadView.dropDocumentsHere')}
        caption={t('projectDetail.uploadView.pdfDocDocxTxtRtf')}
        buttonLabel={t('projectDetail.uploadView.upload')}
        onDrop={onDrop}
      />

      {warnings.length > 0 && (
        <div className="flex flex-col gap-2">
          {warnings.map((warning) => (
            <div
              key={warning.id}
              role="alert"
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-amber-600" />
              <span className="flex-1">{warning.message}</span>
              <button
                type="button"
                onClick={() => onDismissWarning(warning.id)}
                aria-label={t('projectDetail.uploadView.dismissWarning')}
                className="rounded p-0.5 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <XIcon className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {chips.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-muted-foreground">
          <FileTextIcon className="size-6" />
          <span className="text-sm">{t('projectDetail.uploadView.addedFilesWillAppearHere')}</span>
        </div>
      ) : (
        <div className="flex flex-1 min-h-0 flex-col gap-2">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{t('projectDetail.uploadView.filesQueued', { count: chips.length })}</span>
            <span>{formatFileSize(chips.reduce((sum, chip) => sum + chip.file.size, 0))}</span>
          </div>
          <div className="flex flex-1 min-h-0 flex-col gap-2 overflow-y-auto">
            <AnimatePresence>
              {chips.map((chip, index) => (
                <FileListItem
                  key={chip.id}
                  fileName={chip.file.name}
                  fileSize={chip.file.size}
                  fileType={chip.file.type}
                  status={toPresentationStatus(chip.status)}
                  errorMessage={chip.errorMessage}
                  onRemove={() => onRemove(chip.id)}
                  delay={index * 0.05}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}
